import { ChargeTypePolicy } from "constants/charge/ChargeTypePolicy";
import { SubChargeTypePolicy } from "constants/charge/SubChargeTypePolicy";
import { CompanyFspEntity, EMPTY_COMPANY_FSP_ENTITY } from "domain/entity/Company/CompanyFspEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { CompanyFspRepository } from "domain/repository/Company/CompanyFspRepo";
import { CompanyTerminalRepository } from "domain/repository/Company/CompanyTerminalRepo";
import { ForwarderCodeRepository } from "domain/repository/ForwarderCode/ForwarderCodeRepo";
import { FspDirectionRefRepository } from "domain/repository/FspDirectionRef/FspDirectionRefRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { MasterFSPRepository } from "domain/repository/MasterFSP/MasterFSPRepo";
import { OpsLineRepository } from "domain/repository/OpsLine/OpsLineRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import _ from "lodash";
import { commonCompFspBulkValidationSchema } from "presentation/constant/Company/CompFspBulkEditValidationSchema";
import { commonCompFspValidationSchema } from "presentation/constant/Company/CompFspValidationSchema";
import { FspCalculateMethodDroOpts } from "presentation/constant/DropDownOptions/Common/FspCalculateMethodDroOpts";
import { SplitDaysToDroOpts } from "presentation/constant/DropDownOptions/Common/SplitDaysToDroOpts";
import { CntrMovementDroOpts } from "presentation/constant/DropDownOptions/Company/CntrMovementDroOpts";
import { CntrStatusDroOpts } from "presentation/constant/DropDownOptions/Company/CntrStatusDroOpts";
import { CustomerTypeDroOpts } from "presentation/constant/DropDownOptions/Company/CustomerTypeDroOpts";
import { EventTypeDroOpts } from "presentation/constant/DropDownOptions/Company/EventTypeDroOpts";
import { FSPCountingDroOpts } from "presentation/constant/DropDownOptions/Company/FSPCountingDroOpts";
import { FSPUomDroOpts } from "presentation/constant/DropDownOptions/Company/FSPUomDroOpts";
import { Validation } from "presentation/constant/Validation";
import { CompanyDetailModel, EMPTY_COMPANY_FSP_STATE } from "presentation/model/Company/CompanyDetailModel";
import { DropdownProps } from "presentation/model/DropdownProps";

import BaseViewModel from "presentation/viewModel/BaseViewModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";

interface CompanyFspVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<CompanyDetailModel>> | ((value: SetStateAction<CompanyDetailModel>) => void),
    ]
    companyFspRepo: CompanyFspRepository,
    parameterDetailRepo: ParameterDetailRepository,
    opsLineRepo: OpsLineRepository,
    chargeTypeRepo: ChargeTypeRepository,
    forwarderCodeRepo: ForwarderCodeRepository,
    masterDataRepo: MasterDataRepository,
    companyTerminalRepo: CompanyTerminalRepository,
    fspDirectionRefRepo: FspDirectionRefRepository,
    masterFspRepo: MasterFSPRepository,
}

export const CompanyFspVM = ({ dispatch, companyFspRepo, parameterDetailRepo, opsLineRepo, chargeTypeRepo, companyTerminalRepo, fspDirectionRefRepo,
    forwarderCodeRepo, masterDataRepo, masterFspRepo }: CompanyFspVMProps) => {
    const [companyDetailDispatch] = dispatch;

    const loadDropdownOption = async (operatingUnit: string, prevState: CompanyDetailModel/*companyId?: number*/) => {
        prevState.dynamicOptions.calendarAdjDropdownOptions = EventTypeDroOpts().getEventTypeModel();
        prevState.dynamicOptions.customerTypeDropdownOptions = CustomerTypeDroOpts().getCustomerTypeModel();
        prevState.dynamicOptions.splitDaysToDropdownOptions = SplitDaysToDroOpts().getDroOptsModel();

        await masterDataRepo.getMasterDataByKey(MasterDataType.CHARGE_IND).then(
            chargeInds => {
                const chargeIndDropdownOptions = chargeInds?.map((chargeInd) => ({
                    dropdownLabel: chargeInd.code,
                    tagLabel: chargeInd.code,
                    value: chargeInd.code,
                })) ?? []

                prevState.dynamicOptions.chargeIndDropdownOptions = chargeIndDropdownOptions;
            }
        );

        await masterDataRepo.getMasterDataByKey(MasterDataType.CONSORTIUM).then(
            consortiumCodes => {
                const consortiumCodeDropdownOptions = consortiumCodes?.map((consortiumCode) => ({
                    dropdownLabel: consortiumCode.code,
                    tagLabel: consortiumCode.code,
                    value: consortiumCode.code,
                })) ?? []

                prevState.dynamicOptions.consortiumCodeDropdownOptions = consortiumCodeDropdownOptions;

            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.SERVICE).then(
            serviceCodes => {
                const serviceCodeDropdownOptions = serviceCodes?.map((serviceCode) => ({
                    dropdownLabel: serviceCode.code,
                    tagLabel: serviceCode.code,
                    value: serviceCode.code,
                })) ?? []

                prevState.dynamicOptions.serviceCodeDropdownOptions = serviceCodeDropdownOptions;
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("SHIPMENT_TYPE").then(
            shipmentTypes => {
                const shipmentTypeDropdownOptions = shipmentTypes?.map((shipmentType) => ({
                    dropdownLabel: shipmentType.parameterDtlCode,
                    tagLabel: shipmentType.parameterDtlCode,
                    value: shipmentType.parameterDtlCode,
                })) ?? []

                prevState.dynamicOptions.shipmentTypeDropdownOptions = shipmentTypeDropdownOptions;
            }
        )

        await opsLineRepo.getOpsLineForCombobox().then(
            opsLines => {
                const opsLinesDropdownOptions = opsLines?.map((opsLine) => ({
                    dropdownLabel: opsLine.opsLine,
                    tagLabel: opsLine.opsLine,
                    value: opsLine.opsLine,
                })) ?? []

                prevState.dynamicOptions.opsLineDropdownOptions = opsLinesDropdownOptions;
            }
        )

        await forwarderCodeRepo.getAllForwarderCodes().then(
            forwarderCodes => {
                const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
                    dropdownLabel: forwarderCode.forwarderCode,
                    tagLabel: forwarderCode.forwarderCode,
                    value: forwarderCode.forwarderCode,
                })) ?? []

                prevState.dynamicOptions.forwardCodeDropdownOptions = forwarderCodesDropdownOptions;
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode("CARGO_CODE").then(
            cargoCodes => {
                const cargoCodeDropdownOptions = cargoCodes?.map((cargoCode) => ({
                    dropdownLabel: cargoCode.parameterDtlCode,
                    tagLabel: cargoCode.parameterDtlCode,
                    value: cargoCode.parameterDtlCode,
                })) ?? []

                prevState.dynamicOptions.cargoCodeDropdownOptions = cargoCodeDropdownOptions;
            }
        )

        await chargeTypeRepo.getFspChargeType().then(
            chargeTypes => {
                let newChargeTypes = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
                let chargeTypeDropdownOptions: DropdownProps[] = [];
                let subChargeTypeDropdownOptions: { [key: string]: DropdownProps[] } = {};
                newChargeTypes?.forEach(chgTypeEty => {
                    const chgTypeExisted = chargeTypeDropdownOptions?.find(chgType =>
                        chgType.value === chgTypeEty.chargeType);
                    if (!chgTypeExisted) {
                        chargeTypeDropdownOptions.push({
                            dropdownLabel: chgTypeEty.chargeType,
                            tagLabel: chgTypeEty.chargeType,
                            value: chgTypeEty.chargeType
                        })
                    }
                    if (chgTypeEty.subChargeType && chgTypeEty.activeInd === 'Y' && chgTypeEty.chargeCategory !== 'USER' && chgTypeEty.subChargeType !== 'T2'
                        && chgTypeEty.subChargeType !== 'PCIH' && chgTypeEty.subChargeType !== 'PCXH'
                    ) {
                        if (!subChargeTypeDropdownOptions[chgTypeEty.chargeType]) {
                            subChargeTypeDropdownOptions[chgTypeEty.chargeType] = [];
                        }
                        subChargeTypeDropdownOptions[chgTypeEty.chargeType].push({
                            dropdownLabel: chgTypeEty.subChargeType,
                            tagLabel: chgTypeEty.subChargeType,
                            value: chgTypeEty.subChargeType
                        });
                    }
                });

                prevState.dynamicOptions.fspChargeTypeDropdownOptions = chargeTypeDropdownOptions;
                prevState.dynamicOptions.fspSubChargeTypeDropdownOptions = subChargeTypeDropdownOptions;
            }
        )

        await fspDirectionRefRepo.getAllFspDirectionRefComboBox().then(
            fspDirections => {
                let newFspDirectionList = _.orderBy(fspDirections, ["chargeType", "subChargeType"]);
                let newFspDirections = newFspDirectionList.filter(entity => entity.activeInd === 'Y');

                let fspDirectionDropOpts: { [key: string]: DropdownProps[] } = {};
                newFspDirections?.forEach(fspDirection => {
                    let dropOptKey = (fspDirection.chargeType ?? "") + "_" + (fspDirection.subChargeType ?? "");

                    if (!fspDirectionDropOpts[dropOptKey]) {
                        fspDirectionDropOpts[dropOptKey] = [];
                    }
                    fspDirectionDropOpts[dropOptKey].push({
                        dropdownLabel: fspDirection.direction,
                        tagLabel: fspDirection.direction,
                        value: fspDirection.direction
                    });

                });

                prevState.dynamicOptions.directionDropdownOptions = fspDirectionDropOpts;
            }
        )

        await masterFspRepo.getLoadAgentCode().then(
            agentInfos => {
                const agentInfosDropdownOptions = agentInfos?.map((agentInfo) => ({
                    dropdownLabel: agentInfo.agentCode || '',
                    tagLabel: agentInfo.agentCode || '',
                    value: agentInfo.agentCode || '',
                })) ?? []

                prevState.dynamicOptions.agentInfosDropdownOptions = agentInfosDropdownOptions;
            }
        )

        prevState.companyId && await companyTerminalRepo.searchCompanyTerminal(prevState.companyId).then(
            companyTerminals => {
                const operatingTmlDropdownOptions = companyTerminals?.map((companyTerminal) => ({
                    dropdownLabel: companyTerminal.terminal?.terminalCode as string,
                    tagLabel: companyTerminal.terminal?.terminalCode as string,
                    value: companyTerminal.terminal?.terminalCode as string,
                })) ?? []

                prevState.dynamicOptions.operatingTmlDropdownOptions = operatingTmlDropdownOptions;
            }
        )

        prevState = reLoadDropdownOption(operatingUnit, prevState);

        return companyDetailDispatch({ ...prevState/*, forceRefresh: !prevState.forceRefresh*/ });
    }

    const reLoadDropdownOption = (operatingUnit: string, prevState: CompanyDetailModel) => {
        //companyDetailDispatch(prevState => {
        const chargeTypeStr = prevState.companyFspState.currentCompanyFsp?.chargeType;
        const subChargeTypeStr = prevState.companyFspState.currentCompanyFsp?.subChargeType;

        const chargeTypeDropOpts = prevState.dynamicOptions.fspChargeTypeDropdownOptions;

        const chargeType = chargeTypeStr ? chargeTypeStr : (chargeTypeDropOpts && chargeTypeDropOpts.length > 0 ? chargeTypeDropOpts[0].value : "");
        const subChargeTypeDropOpts = chargeType ? prevState.dynamicOptions.fspSubChargeTypeDropdownOptions[chargeType] : [];

        const subChargeType = subChargeTypeStr ? subChargeTypeStr : (subChargeTypeDropOpts && subChargeTypeDropOpts.length > 0 ? subChargeTypeDropOpts[0].value : "");

        prevState = initDropOptsByChg(prevState, chargeType, subChargeType, operatingUnit);

        return prevState;
    }

    const initDropOptsByChg = (prevState: CompanyDetailModel, chgType: string, subChgType: string, operatingUnit: string) => {
        const fspCalcMethodDroOpts = initFspCalculateMethod(chgType, subChgType);
        const fspUomDroOpts = initFspUom(chgType, subChgType, operatingUnit);

        const newChgType = getChargeTypeForCC(chgType, subChgType);
        let fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
        let fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
        if (subChgType && !("CC" === chgType || "DEM" === chgType)) {
            fspCountingFromDroOpts = initFspCountingFromBySubChgType(chgType, subChgType, operatingUnit);
            fspCountingToDroOpts = initFspCountingToBySubChgType(chgType, subChgType, operatingUnit);
        }

        const cntrMovementDroOpts = initCntrMovement(newChgType);
        const cntrStatusDroOpts = initCntrStatus(newChgType, "");

        prevState.companyFspState.forceRefresh = !prevState.companyFspState.forceRefresh;
        prevState.dynamicOptions = {
            ...prevState.dynamicOptions,

            cntrMovementDropdownOptions: cntrMovementDroOpts,
            cntrStatusDropdownOptions: cntrStatusDroOpts,
            fspCountingFromDropdownOptions: fspCountingFromDroOpts,
            fspCountingToDropdownOptions: fspCountingToDroOpts,
            fspUomDropdownOptions: fspUomDroOpts,
            fspCalcMethodDropdownOptions: fspCalcMethodDroOpts
        }

        return prevState;
    }

    const onInitDefaultValue = (prevState: CompanyDetailModel) => {
        return companyDetailDispatch({ ...onInitFormValue(prevState)/*, forceRefresh: !prevState.forceRefresh*/ });
    }

    const onInitFormValue = (prevState: CompanyDetailModel) => {
        const chargeTypeStr = prevState.companyFspState.currentCompanyFsp?.chargeType;
        const subChargeTypeStr = prevState.companyFspState.currentCompanyFsp?.subChargeType;

        const chargeTypeDropOpts = prevState.dynamicOptions.fspChargeTypeDropdownOptions;

        const chargeType = chargeTypeStr ? chargeTypeStr : (chargeTypeDropOpts && chargeTypeDropOpts.length > 0 ? chargeTypeDropOpts[0].value : "");
        const subChargeTypeDropOpts = chargeType ? prevState.dynamicOptions.fspSubChargeTypeDropdownOptions[chargeType] : [];

        const subChargeType = subChargeTypeStr ? subChargeTypeStr : (subChargeTypeDropOpts && subChargeTypeDropOpts.length > 0 ? subChargeTypeDropOpts[0].value : "");

        const fspCalcMethodDroOpts = prevState.dynamicOptions.fspCalcMethodDropdownOptions;
        const fspUomDroOpts = prevState.dynamicOptions.fspUomDropdownOptions;

        const fspCountingFromDroOpts = prevState.dynamicOptions.fspCountingFromDropdownOptions;
        const fspCountingToDroOpts = prevState.dynamicOptions.fspCountingToDropdownOptions;

        let defFspCalcMethod: string = "", defFspUom: string = "",
            defFspCountingFrm: string = "", defFspCountingTo: string = "",
            defFspCountingFrmVal: string = "", defFspCountingToVal: string = "",
            defFspDirection: string = "";
        if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
            defFspCountingFrm = fspCountingFromDroOpts[0].tagLabel;
            defFspCountingFrmVal = fspCountingFromDroOpts[0].value;
        }
        if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
            defFspCountingTo = fspCountingToDroOpts[0].tagLabel;
            defFspCountingToVal = fspCountingToDroOpts[0].value;
        }

        const directionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chargeType + "_" + subChargeType)];
        const tempDirectionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chargeType + "_")]
        const newDirectionDroOpts = _.unionBy(directionDroOpts, tempDirectionDroOpts, "value");

        if (newDirectionDroOpts && newDirectionDroOpts.length > 0) {
            defFspDirection = newDirectionDroOpts[0].value;
        }

        if (fspCalcMethodDroOpts && fspCalcMethodDroOpts.length > 0) {
            defFspCalcMethod = fspCalcMethodDroOpts[0].value
        }

        if (fspUomDroOpts && fspUomDroOpts.length > 0) {
            defFspUom = fspUomDroOpts[0].value
        }

        prevState.companyFspState.forceRefresh = !prevState.companyFspState.forceRefresh;
        prevState.companyFspState = {
            ...prevState.companyFspState,
            currentCompanyFsp: {
                ...prevState.companyFspState.currentCompanyFsp,
                chargeType: chargeType,
                subChargeType: subChargeType,
                fspCalculateMethod: defFspCalcMethod,
                fspUOM: defFspUom,
                fspCountingFrom: defFspCountingFrm,
                fspCountingFromVal: defFspCountingFrmVal,
                fspCountingToVal: defFspCountingToVal,
                fspCountingTo: defFspCountingTo,
                fspCountingDirection: defFspDirection,
                splitFirstDays: null,
                splitFirstDaysTo: null,
                splitRemainDaysTo: null
            }
        }

        prevState.viewState.allFormState = {
            ...prevState.viewState.allFormState,
            fspCalculateMethod: '',
            fspUOM: '',
            fspCountingFrom: '',
            fspCountingTo: '',
            fspCountingDirection: '',
            splitFirstDays: '',
            splitFirstDaysTo: '',
            splitRemainDaysTo: ''
        }

        return prevState;
    }

    const onFieldChange = (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));

            if (val && (fieldKey === "owner" || fieldKey === "shipmentType" || fieldKey === "agentCode"
                || fieldKey === "applyCalendarAdj" || fieldKey === "forwarderCode")
            ) {
                val = val.join(",");
            }
        }

        if (val && (fieldKey === "graceCountHourFrom" || fieldKey === "graceCountHourTo" ||
            fieldKey === "graceCountMinFrom" || fieldKey === "graceCountMinTo")) {
            val = val as unknown as number;
        }

        return companyDetailDispatch(prevState => {
            return {
                ...prevState,
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [fieldKey]: val
                    }
                },
                viewState: {
                    ...prevState.viewState,
                    allFormState: {
                        ...prevState.viewState.allFormState,
                        [fieldKey]: "",
                    }
                }
            }
        });
    }

    const onBulkEditFieldChange = (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        if (val && (fieldKey === "graceCountHourFrom" || fieldKey === "graceCountHourTo" ||
            fieldKey === "graceCountMinFrom" || fieldKey === "graceCountMinTo")) {
            val = val as unknown as number;
        }

        return companyDetailDispatch(prevState => {
            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh,
                companyFspState: {
                    ...prevState.companyFspState,
                    [fieldKey]: val
                },
                allFormState: {
                    ...prevState.allFormState,
                    [fieldKey]: ""
                }
            }
        });
    }

    const onDropdownChange = (e: any, fieldName: string) => {
        let val: any = e;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        } else if (e) {
            val = e?.value;
        }
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh,
                companyFspState: {
                    ...prevState.companyFspState,
                    [fieldName]: val,
                }
            }
        });
    }

    const onCompanyFSPFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        companyDetailDispatch(prevState => {

            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh,
                companyFspState: {
                    ...prevState.companyFspState,
                    [fieldKey]: val,
                },
            }
        })
    }


    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        companyDetailDispatch(prevState => ({
            ...prevState,
            companyFspState: {
                ...prevState.companyFspState,
                currentCompanyFsp: {
                    ...prevState.companyFspState.currentCompanyFsp,
                    [fieldName]: checked ? "Y" : "N"
                }
            }
        }))
    }

    const onChargeTypeChange = (fieldKey: string, fieldValue: IFieldValue, operatingUnit: string, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        const chgType = val;
        companyDetailDispatch(prevState => {
            const subChgTypeOptions = chgType ? prevState.dynamicOptions.fspSubChargeTypeDropdownOptions[chgType] : [];
            const subChgType = subChgTypeOptions && subChgTypeOptions.length > 0 ? subChgTypeOptions[0].value : "";

            //const directionDroOpts = initFspDirection(chgType, subChgType);
            const fspCalcMethodDroOpts = initFspCalculateMethod(chgType, subChgType);
            const fspUomDroOpts = initFspUom(chgType, subChgType, operatingUnit);

            const newChgType = getChargeTypeForCC(chgType, subChgType);
            const fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
            const fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
            const cntrMovementDroOpts = initCntrMovement(newChgType);
            const cntrStatusDroOpts = initCntrStatus(newChgType, "");

            let defFspCalcMethod, defFspUom, defFspCountingFrm, defFspCountingFrmVal, defFspCountingTo, defFspCountingToVal, defFspDirection;
            if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
                defFspCountingFrm = fspCountingFromDroOpts[0].tagLabel;
                defFspCountingFrmVal = fspCountingFromDroOpts[0].value;
            }
            if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
                defFspCountingTo = fspCountingToDroOpts[0].tagLabel;
                defFspCountingToVal = fspCountingToDroOpts[0].value;
            }

            const directionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_" + subChgType)];
            const tempDirectionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_")]
            const newDirectionDroOpts = _.unionBy(directionDroOpts, tempDirectionDroOpts, "value");

            if (newDirectionDroOpts && newDirectionDroOpts.length > 0) {
                defFspDirection = newDirectionDroOpts[0].value;
            }

            if (fspCalcMethodDroOpts && fspCalcMethodDroOpts.length > 0) {
                defFspCalcMethod = fspCalcMethodDroOpts[0].value
            }

            if (fspUomDroOpts && fspUomDroOpts.length > 0) {
                defFspUom = fspUomDroOpts[0].value
            }

            const allFormState = prevState.viewState.allFormState || {};
            return {
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    //directionDropdownOptions: directionDroOpts,
                    fspCountingFromDropdownOptions: fspCountingFromDroOpts,
                    fspCountingToDropdownOptions: fspCountingToDroOpts,
                    cntrMovementDropdownOptions: cntrMovementDroOpts,
                    cntrStatusDropdownOptions: cntrStatusDroOpts,
                    fspCalcMethodDropdownOptions: fspCalcMethodDroOpts,
                    fspUomDropdownOptions: fspUomDroOpts
                },
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [fieldKey]: chgType,
                        subChargeType: subChgType ?? null,
                        fspCalculateMethod: defFspCalcMethod ?? null,
                        fspUOM: defFspUom ?? null,
                        fspCountingFrom: defFspCountingFrm ?? null,
                        fspCountingTo: defFspCountingTo ?? null,
                        fspCountingFromVal: defFspCountingFrmVal ?? null,
                        fspCountingToVal: defFspCountingToVal ?? null,
                        fspCountingDirection: defFspDirection ?? null,
                        splitFirstDays: null,
                        splitFirstDaysTo: null,
                        splitRemainDaysTo: null
                    }
                },
                viewState: {
                    ...prevState.viewState,
                    allFormState: {
                        ...prevState.viewState.allFormState,
                        [fieldKey]: "",
                        fspCountingTo: defFspCountingTo ? "" : allFormState["fspCountingTo"],
                        fspCountingFrom: defFspCountingFrm ? "" : allFormState["fspCountingFrom"],
                        fspCountingFromVal: defFspCountingFrmVal ? "" : allFormState["fspCountingFromVal"],
                        fspCountingToVal: defFspCountingToVal ? "" : allFormState["fspCountingToVal"],
                        fspCountingDirection: defFspDirection ? "" : allFormState["fspCountingDirection"],
                    }
                }
            }
        });
    }

    const onSubChargeTypeChange = (fieldKey: string, chgType: string, subChargeType: string, operatingUnit: string) => {
        let val: any = subChargeType;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        const subChgType = val;

        let fspCountingFromDroOpts: DropdownProps[], fspCountingToDroOpts: DropdownProps[],
            cntrMovementDroOpts: DropdownProps[], cntrStatusDroOpts: DropdownProps[];
        let isResetDroOpts = false;
        if ("CC" === chgType || "DEM" === chgType) {
            isResetDroOpts = true;
            //directionDroOpts = initFspDirection(chgType, subChgType);

            const newChgType = getChargeTypeForCC(chgType, subChgType);
            fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
            fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
            cntrMovementDroOpts = initCntrMovement(newChgType);
            cntrStatusDroOpts = initCntrStatus(newChgType, "");
        } else {
            fspCountingFromDroOpts = initFspCountingFromBySubChgType(chgType, subChgType, operatingUnit);
            fspCountingToDroOpts = initFspCountingToBySubChgType(chgType, subChgType, operatingUnit);
        }

        const fspUomDroOpts = initFspUom(chgType, subChgType, operatingUnit);
        const fspCalcMethodDroOpts = initFspCalculateMethod(chgType, subChgType);

        /*const fspCountingFromDroOpts = initFspCountingFromBySubChgType(chargeType, subChargeType, operatingUnit);
        const fspCountingToDroOpts = initFspCountingToBySubChgType(chargeType, subChargeType, operatingUnit);*/

        return companyDetailDispatch(prevState => {
            let defFspCalcMethod, defFspUom, defFspCountingFrm, defFspCountingFrmVal, defFspCountingTo, defFspCountingToVal, defFspDirection;

            if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
                defFspCountingFrm = fspCountingFromDroOpts[0].tagLabel;
                defFspCountingFrmVal = fspCountingFromDroOpts[0].value;
            }
            if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
                defFspCountingTo = fspCountingToDroOpts[0].tagLabel;
                defFspCountingToVal = fspCountingToDroOpts[0].value;
            }

            const directionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_" + subChgType)];
            const tempDirectionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_")]
            const newDirectionDroOpts = _.unionBy(directionDroOpts, tempDirectionDroOpts, "value");

            if (newDirectionDroOpts && newDirectionDroOpts.length > 0) {
                defFspDirection = newDirectionDroOpts[0].value;
            }

            if (fspCalcMethodDroOpts && fspCalcMethodDroOpts.length > 0) {
                defFspCalcMethod = fspCalcMethodDroOpts[0].value
            }

            if (fspUomDroOpts && fspUomDroOpts.length > 0) {
                defFspUom = fspUomDroOpts[0].value
            }

            return {
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    cntrMovementDropdownOptions: isResetDroOpts ? cntrMovementDroOpts : prevState.dynamicOptions.cntrMovementDropdownOptions,
                    cntrStatusDropdownOptions: isResetDroOpts ? cntrStatusDroOpts : prevState.dynamicOptions.cntrStatusDropdownOptions,
                    fspCountingFromDropdownOptions: fspCountingFromDroOpts,
                    fspCountingToDropdownOptions: fspCountingToDroOpts,
                    fspUomDropdownOptions: fspUomDroOpts,
                    fspCalculateMethodDropdownOptions: fspCalcMethodDroOpts
                },
                viewState: {
                    ...prevState.viewState,
                    allFormState: {
                        ...prevState.viewState.allFormState,
                        [fieldKey]: "",
                    }
                },
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [fieldKey]: val,
                        fspCalculateMethod: defFspCalcMethod ?? null,
                        fspUOM: defFspUom ?? null,
                        fspCountingFrom: defFspCountingFrm ?? null,
                        fspCountingTo: defFspCountingTo ?? null,
                        fspCountingFromVal: defFspCountingFrmVal ?? null,
                        fspCountingToVal: defFspCountingToVal ?? null,
                        fspCountingDirection: defFspDirection ?? null,
                        splitFirstDays: null,
                        splitFirstDaysTo: null,
                        splitRemainDaysTo: null
                    }
                }
            }
        });
    }

    const onCntrMovementChange = (fieldKey: string, chargeType: string, cntrMovement: string) => {
        let val: any = cntrMovement;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        const newChargeType = getChargeTypeForCC(chargeType, "")
        const cntrStatusDroOpts = initCntrStatus(newChargeType, cntrMovement);

        return companyDetailDispatch(prevState => {
            return {
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    cntrStatusDropdownOptions: cntrStatusDroOpts
                },
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [fieldKey]: val
                    }
                }
            }
        });
    }

    // const initFspDirection = (chargeType: string) => {
    //     return FSPDirectionDroOpts().getDirectionByChargeType(chargeType);
    // }

    const initFspCountingFrom = (chargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChargeType(chargeType);
    }

    const initFspCountingTo = (chargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingToByChargeType(chargeType/*, operatingUnit*/);
    }

    const initCntrMovement = (chargeType: string) => {
        if (chargeType === ChargeTypePolicy.CHARGE_TYPE_ITM
            || chargeType === ChargeTypePolicy.CHARGE_TYPE_OBS
            || chargeType === ChargeTypePolicy.CHARGE_TYPE_TSS
        ) {
            return CntrMovementDroOpts().getDroOptsModel(false);
        } else {
            return CntrMovementDroOpts().getDroOptsModel(true);
        }
    }

    const initCntrStatus = (chargeType: string, cntrMovement: string) => {
        return CntrStatusDroOpts().getDroOptsModelByMovementAndChgType(chargeType, cntrMovement, "");
    }

    const getChargeTypeForCC = (chargeType: string, subChargeType: string) => {
        let newChargeType;

        if (chargeType && chargeType === ChargeTypePolicy.CHARGE_TYPE_CC) {
            if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_OBS) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_OBS;
            } else if (subChargeType === SubChargeTypePolicy.CODE_ITM) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_ITM;
            } else {
                newChargeType = chargeType;
            }
        } else if (chargeType && chargeType === ChargeTypePolicy.CHARGE_TYPE_DEM) {
            if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_DEX) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_OBS;
            } else if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_DEI) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_ITM;
            } else {
                newChargeType = chargeType;
            }
        } else {
            newChargeType = chargeType;
        }

        return newChargeType;
    }

    const initFspCalculateMethod = (chargeType: string, subChargeType: string) => {
        return FspCalculateMethodDroOpts().getDroOptsModel(chargeType, subChargeType);
    }

    const initFspUom = (chargeType: string, subChargeType: string, operatingUnit: string) => {
        if ("RFH" === chargeType && "T1" === subChargeType) {
            return FSPUomDroOpts(operatingUnit).getFspUomModelForSpecialChg(chargeType, subChargeType);
        } else if ("RFH" === chargeType && ("PCI" === subChargeType || "PCX" === subChargeType)) {
            return FSPUomDroOpts(operatingUnit).getAllFspUomModel();
        } else {
            return FSPUomDroOpts(operatingUnit).getFspUomNoHfDayModel();
        }
    }

    const initFspCountingFromBySubChgType = (chargeType: string, subChargeType: string, operatingUnit: string) => {
        //return FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChgTypeAndSubChgType(chargeType, subChargeType);
        if (operatingUnit === "SAPT") {
            return FSPCountingDroOpts(operatingUnit).getFSPCountingFromBySubChargeType(subChargeType);
        } else {
            return FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChgTypeAndSubChgType(chargeType, subChargeType);
        }
    }

    const initFspCountingToBySubChgType = (chargeType: string, subChargeType: string, operatingUnit: string) => {
        //return FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChgTypeAndSubChgType(chargeType, subChargeType);
        if (operatingUnit === "SAPT") {
            return FSPCountingDroOpts(operatingUnit).getFSPCountingToBySubChargeType(subChargeType);
        } else {
            return FSPCountingDroOpts(operatingUnit).getFSPCountingToByChgTypeAndSubChgType(chargeType, subChargeType);
        }
    }

    const onFspCountingChange = (fieldKey: string, fieldValue: IFieldValue, operatingUnit: string, fFullValue?: any, dropKeyField?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        return companyDetailDispatch(prevState => {
            const chargeType = prevState.companyFspState.currentCompanyFsp.chargeType ?? "";
            const subChargeType = prevState.companyFspState.currentCompanyFsp.subChargeType ?? "";
            const newChageType = getChargeTypeForCC(chargeType, subChargeType);

            let dropKeyVal;
            if (dropKeyField === "fspCountingFrom") {
                /*val = FSPCountingDroOpts(operatingUnit??"").getFromKeyByValue(newChageType, val);

                if (val === null || val === "") {
                    const fspCountingFromDroOpts = prevState.dynamicOptions.fspCountingFromDropdownOptions;
                    if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
                        val = fspCountingFromDroOpts[0].tagLabel ?? "";
                    }
                }*/
                dropKeyVal = FSPCountingDroOpts(operatingUnit).getFromKeyByValue(newChageType, val);
            }

            if (dropKeyField === "fspCountingTo") {
                /*val = FSPCountingDroOpts(operatingUnit??"").getToKeyByValue(newChageType, val);

                if (val === null || val === "") {
                    const fspCountingToDroOpts = prevState.dynamicOptions.fspCountingToDropdownOptions;
                    if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
                        val = fspCountingToDroOpts[0].tagLabel ?? "";
                    }
                }*/
                dropKeyVal = FSPCountingDroOpts(operatingUnit).getToKeyByValue(newChageType, val);
            }

            return {
                ...prevState,
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [fieldKey]: val,
                        ...(dropKeyField && { [dropKeyField]: dropKeyVal })
                    }
                },
                viewState: {
                    ...prevState.viewState,
                    allFormState: {
                        ...prevState.viewState.allFormState,
                        [fieldKey]: "",
                        ...(dropKeyField && { [dropKeyField]: "" })
                    }
                }
            }
        });
    }

    const onTimePickerChange = (value: Date, hourName: string, minName: string) => {
        const hours = value.getHours();
        const mins = value.getMinutes();

        return companyDetailDispatch(prevState => {
            return {
                ...prevState,
                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: {
                        ...prevState.companyFspState.currentCompanyFsp,
                        [hourName]: hours,
                        [minName]: mins
                    }
                }
            }
        });
    }

    const onAdd = () => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                isShowRightPanel: true,
                viewState: {
                    ...prevState.viewState,
                    isAdd: true,
                    isEditable: false,
                    isSaveClicked: false,
                    isShowFsp: true,
                    isShowEmptyPool: false,
                    isShowSuppleTerms: false,
                    isShowCustAssign: false,
                    isShowFactor: false,
                    isShowCustInfo: false,
                    allFormState: {},
                },

                companyFspState: {
                    ...prevState.companyFspState,
                    selectedDatas: [],
                    currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                }
            }
        });
    }

    const onEdit = (currentCompanyFsp: CompanyFspEntity) => {
        companyDetailDispatch(prevState => {

            return {
                ...prevState,
                isShowRightPanel: true,
                viewState: {
                    ...prevState.viewState,
                    isAdd: false,
                    isEditable: prevState.viewState.isEditCompDtlInfo,
                    isSaveClicked: false,
                    isShowFsp: true,
                    isShowEmptyPool: false,
                    isShowSuppleTerms: false,
                    isShowCustAssign: false,
                    isShowFactor: false,
                    isShowCustInfo: false,
                    allFormState: {}
                },

                companyFspState: {
                    ...prevState.companyFspState,
                    currentCompanyFsp: currentCompanyFsp,
                }
            }
        });
    }

    const handleDelete = () => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                isShowRightPanel: false,

                viewState: {
                    ...prevState.viewState,
                    isShowDeleteModal: true,
                    dataType: "FSP"
                }
            }
        });
    }

    const onDelete = async (selectedRows: CompanyFspEntity[]) => {
        await companyFspRepo.deleteCompanyFsp(
            selectedRows.map(selectedRow => selectedRow.id) as number[]
        ).then((res) => {


            companyDetailDispatch(prevState => {
                return {
                    ...prevState,
                    isShowRightPanel: false,
                    viewState: {
                        ...prevState.viewState,
                        isShowDeleteModal: false,
                        dataType: ""
                    },

                    companyFspState: {
                        ...prevState.companyFspState,
                        isDeleteRefresh: true,
                        selectedDatas: [],
                        currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                    }
                }
            });
        })
    }

    const onSubmit = async (selectedRows: CompanyFspEntity[]) => {
        const updatedRows = selectedRows.map((selectedRow) => {
            return { ...selectedRow, status: 'FINALIZED', confirmedDate: null };
        });

        await companyFspRepo.batchUpdateCompanyFsp(updatedRows).then((res) => {


            companyDetailDispatch(prevState => {
                return {
                    ...prevState,
                    isShowRightPanel: false,

                    companyFspState: {
                        ...prevState.companyFspState,
                        selectedDatas: [],
                        currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                    }
                }
            });
        })
    }

    const handleReject = () => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                isShowRightPanel: false,

                viewState: {
                    ...prevState.viewState,
                    isShowRejectModal: true,
                    dataType: "FSP"
                }
            }
        });
    }

    const onReject = async (selectedRows: CompanyFspEntity[], rejectReason: string) => {
        const updatedRows = selectedRows.map((selectedRow) => {
            return { ...selectedRow, status: 'REJECTED', rejectReason: rejectReason };
        });

        await companyFspRepo.batchUpdateCompanyFsp(updatedRows).then((res) => {


            companyDetailDispatch(prevState => {
                return {
                    ...prevState,
                    isShowRightPanel: false,

                    viewState: {
                        ...prevState.viewState,
                        isShowRejectModal: false,
                        dataType: "",
                        rejectReason: ""
                    },

                    companyFspState: {
                        ...prevState.companyFspState,
                        selectedDatas: [],
                        currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                    }
                }
            });
        })
    }

    const onApprove = async (selectedRows: CompanyFspEntity[]) => {
        const confirmDate = new Date();
        const updatedRows = selectedRows.map((selectedRow) => {
            return { ...selectedRow, confirmedDate: confirmDate, rejectReason: null };
        });

        await companyFspRepo.batchUpdateCompanyFsp(updatedRows).then((res) => {
            companyDetailDispatch(prevState => {
                return {
                    ...prevState,
                    isShowRightPanel: false,

                    companyFspState: {
                        ...prevState.companyFspState,
                        selectedDatas: [],
                        currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                    }
                }
            });
        })
    }

    const onApply = async (selectedRows: CompanyFspEntity[]) => {
        return await companyFspRepo.batchUpdateCompanyFsp(selectedRows);
    }

    const onFspRowDoubleClick = (row: CompanyFspEntity) => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                isShowRightPanel: true,
                viewState: {
                    ...prevState.viewState,
                    //isAdd: false,
                    isEditable: prevState.viewState.isEditCompDtlInfo,
                    isSaveClicked: false,
                    isShowFsp: true,
                    isShowEmptyPool: false,
                    isShowSuppleTerms: false,
                    isShowCustAssign: false,
                    isShowFactor: false,
                    isShowCustInfo: false,
                    allFormState: {}
                },

                companyFspState: {
                    ...prevState.companyFspState,
                    forceRefresh: !prevState.companyFspState.forceRefresh,
                    currentCompanyFsp: row
                }
            }
        })
    }
    const onSelectedCompFsps = (selectedRows: CompanyFspEntity[]) => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,

                companyFspState: {
                    ...prevState.companyFspState,
                    selectedDatas: selectedRows,
                }
            }
        })
    }

    const onSave = async (currentCompanyFsp: CompanyFspEntity, isAdd: boolean, companyDtlState: CompanyDetailModel) => {
        const valResult = await Validation(commonCompFspValidationSchema).ValidateFormOnly(currentCompanyFsp);

        if (valResult) {
            let allValResult: { [x: string]: string } = {};

            if (valResult) {
                allValResult = {
                    ...allValResult, ...valResult,
                    // mandatoryCheckFail: 'Please input the missing value.'
                };
            }

            companyDtlState.viewState.allFormState = { ...allValResult };
            companyDetailDispatch({ ...companyDtlState })
            return { "saveCompanyFspFailed": ""/*'Please input the missing value.'*/ };
        } else {
            await (isAdd ? companyFspRepo.addCompanyFsp(currentCompanyFsp) : companyFspRepo.updateCompanyFsp(currentCompanyFsp)).then((data) => {
                if (data && data.toString().startsWith("Error:")) {
                    companyDetailDispatch(prevState => {
                        return {
                            ...prevState,
                            allFormState: { "saveCompEmptyPoolFailed": data.toString() }
                        };
                    });
                    return { "saveCompEmptyPoolFailed": data.toString() };
                } else {
                    return companyDetailDispatch(prevState => {
                        return {
                            ...prevState,
                            isShowRightPanel: false,
                            viewState: {
                                ...prevState.viewState,
                                isAdd: false,
                                isRead: true,
                                isEditable: false,
                                isSaveClicked: false,
                                lastEditRowId: '',
                                isSliderOpen: false,
                                allFormState: {},

                                isShowFsp: false,
                                isShowEmptyPool: false,
                                isShowSuppleTerms: false,
                                isShowCustAssign: false,
                                isShowFactor: false,
                                isShowCustInfo: false
                            },

                            companyFspState: {
                                ...prevState.companyFspState,
                                selectedDatas: [],
                                currentCompanyFsp: { ...EMPTY_COMPANY_FSP_ENTITY }
                            }
                        }
                    })
                }
            })
        }
    }

    const initialFspTableData = async (compnayId: number) => {
        await companyFspRepo.searchCompanyFsp(compnayId).then(data => {
            companyDetailDispatch(prevState => {
                return {
                    ...prevState,
                    companyFsps: data,
                    companyFspState: {
                        ...prevState.companyFspState,
                        forceRefresh: !prevState.companyFspState.forceRefresh,
                    }
                }
            })
        })
    }

    const onAccordionPanelChange = (e: boolean, fieldKey: string) => {
        companyDetailDispatch(prevState => ({
            ...prevState,
            companyFspState: {
                ...prevState.companyFspState,
                [fieldKey]: e,
            }
        }))
    };

    const onBulkEditClick = () => {
        companyDetailDispatch(prevState => {
            return {
                ...prevState,
                isShowRightPanel: true,
                forceRefresh: !prevState.forceRefresh,
                allFormState: {},
                viewState: {
                    ...prevState.viewState,
                    isAdd: false,
                    isEditable: true,
                    isSaveClicked: false,
                    isShowFsp: false,
                    isShowEmptyPool: false,
                    isShowSuppleTerms: false,
                    isShowCustAssign: false,
                    isShowFactor: false,
                    isShowCustInfo: false,
                    isShowFspBulkEdit: true,
                    allFormState: {}
                },
                companyFspState: {
                    ...prevState.companyFspState,
                    tierFSPEnabled: true,
                    tierFSPVisiable: false,
                    tierFSPUOM: null,
                    firstTierFSP: null,
                    secondTierFSP: null,
                    countingEnabled: true,
                    countingVisiable: false,
                    countingFrom: null,
                    countingTo: null,
                    calendarAdjEnable: true,
                    calendarAdjVisiable: false,
                    calendarAdj: null,
                    gracePeriodEnabled: true,
                    gracePeriodVisiable: false,
                    graceCountHourFrom: null,
                    graceCountHourTo: null,
                    graceCountMinFrom: null,
                    graceCountMinTo: null,
                    chargeType: null,
                    subChargeType: null,
                }
            }
        });
    }

    const onInitBulkEdiPanel = (operatingUnit: string) => {
        companyDetailDispatch(prevState => {
            const chargeType = prevState?.companyFspState?.selectedDatas[0].chargeType ?? "";
            const subChargeType = prevState?.companyFspState?.selectedDatas[0].subChargeType ?? "";

            const fspUomDroOpts = initFspUom(chargeType, subChargeType, operatingUnit);
            const newChgType = getChargeTypeForCC(chargeType, subChargeType);
            let fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
            let fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
            if (subChargeType && !("CC" === chargeType || "DEM" === chargeType)) {
                fspCountingFromDroOpts = initFspCountingFromBySubChgType(chargeType, subChargeType, operatingUnit);
                fspCountingToDroOpts = initFspCountingToBySubChgType(chargeType, subChargeType, operatingUnit);
            }

            return {
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    fspUomDropdownOptions: fspUomDroOpts,
                    fspCountingFromDropdownOptions: fspCountingFromDroOpts,
                    fspCountingToDropdownOptions: fspCountingToDroOpts,
                    calendarAdjDropdownOptions: prevState.dynamicOptions.calendarAdjDropdownOptions
                },
                companyFspState: {
                    ...prevState.companyFspState,
                    chargeType: chargeType,
                    subChargeType: subChargeType,
                }
            }
        });
    }

    const saveBulkEdit = async (companyDtlModel: CompanyDetailModel, operatingUnit: string) => {
        const currentCompanyFspState = companyDtlModel.companyFspState;
        const selectedDatas = currentCompanyFspState.selectedDatas;

        if (currentCompanyFspState.tierFSPVisiable || currentCompanyFspState.gracePeriodVisiable) {
            const valResult = await Validation(commonCompFspBulkValidationSchema).ValidateFormOnly(currentCompanyFspState);
            if (valResult) {
                companyDetailDispatch(prevState => {
                    return {
                        ...prevState,
                        allFormState: valResult
                    }
                })
                return;
            }
        }

        //update bulk edit value into company fsp
        if (_.isEmpty(selectedDatas)) return;
        const updateDatas = selectedDatas.map((data) => {
            if (currentCompanyFspState.calendarAdjVisiable && currentCompanyFspState.calendarAdj) {
                data.applyCalendarAdj = currentCompanyFspState.calendarAdj?.join(",");
            }
            if (currentCompanyFspState.gracePeriodVisiable && (currentCompanyFspState.graceCountHourFrom ||
                currentCompanyFspState.graceCountMinFrom || currentCompanyFspState.graceCountHourTo ||
                currentCompanyFspState.graceCountMinTo)
            ) {
                data.graceHourFrom = currentCompanyFspState.graceCountHourFrom;
                data.graceMinuteFrom = currentCompanyFspState.graceCountMinFrom;
                data.graceHourTo = currentCompanyFspState.graceCountHourTo;
                data.graceMinuteTo = currentCompanyFspState.graceCountMinTo;
            }
            if (currentCompanyFspState.tierFSPVisiable) {
                if (currentCompanyFspState.firstTierFSP) {
                    data.firstTierFSP = currentCompanyFspState.firstTierFSP;
                }
                if (currentCompanyFspState.secondTierFSP) {
                    data.secondTierFSP = currentCompanyFspState.secondTierFSP;
                }
                if (currentCompanyFspState.tierFSPUOM) {
                    data.fspUOM = currentCompanyFspState.tierFSPUOM;
                }
            }
            const newChageType = getChargeTypeForCC(currentCompanyFspState.chargeType ?? "",
                currentCompanyFspState.subChargeType ?? "");
            if (currentCompanyFspState.countingVisiable) {
                if (currentCompanyFspState.countingFrom) {
                    data.fspCountingFrom = FSPCountingDroOpts(operatingUnit).getFromKeyByValue(
                        newChageType ?? "",
                        currentCompanyFspState.countingFrom);
                }
                if (currentCompanyFspState.countingTo) {
                    data.fspCountingTo = FSPCountingDroOpts(operatingUnit).getToKeyByValue(
                        newChageType ?? "",
                        currentCompanyFspState.countingTo);
                }
            }

            return data;
        });

        return await companyFspRepo.batchUpdateCompanyFsp(updateDatas).then((data) => {
            if (data && data.toString().startsWith("Error:")) {
                companyDetailDispatch(prevState => {
                    return {
                        ...prevState,
                        allFormState: { "fspBulkEditFailed": data.toString() }
                    };
                });
                return { "fspBulkEditFailed": data.toString() };
            } else {
                return companyDetailDispatch(prevState => {
                    return {
                        ...prevState,
                        isShowRightPanel: false,
                        viewState: {
                            ...prevState.viewState,
                            isAdd: false,
                            isRead: true,
                            isEditable: false,
                            isSaveClicked: false,
                            lastEditRowId: '',
                            isSliderOpen: false,
                            allFormState: {},

                            isShowFsp: false,
                            isShowEmptyPool: false,
                            isShowSuppleTerms: false,
                            isShowCustAssign: false,
                            isShowFactor: false,
                            isShowCustInfo: false,
                            isShowFspBulkEdit: false,
                        },

                        companyFspState: {
                            ...prevState.companyFspState,
                            ...EMPTY_COMPANY_FSP_STATE
                        }
                    }
                })
            }
        })

    }

    return {
        loadDropdownOption: loadDropdownOption,
        onAdd: onAdd,
        onEdit: onEdit,
        onDelete: onDelete,
        onSubmit: onSubmit,
        onReject: onReject,
        onApprove: onApprove,
        onApply: onApply,
        onSelectedCompFsps: onSelectedCompFsps,
        onFieldChange: onFieldChange,
        onTimePickerChange: onTimePickerChange,
        onSave: onSave,
        initialFspTableData: initialFspTableData,
        handleReject: handleReject,
        onChargeTypeChange: onChargeTypeChange,
        onSubChargeTypeChange: onSubChargeTypeChange,
        onCntrMovementChange: onCntrMovementChange,
        onFspCountingChange: onFspCountingChange,
        onInitDefaultValue: onInitDefaultValue,
        handleDelete: handleDelete,
        onCheckboxChange: onCheckboxChange,
        onFspRowDoubleClick: onFspRowDoubleClick,
        onAccordionPanelChange: onAccordionPanelChange,
        onDropdownChange: onDropdownChange,
        onBatchEditClick: onBulkEditClick,
        onInitBulkEdiPanel: onInitBulkEdiPanel,
        saveBulkEdit: saveBulkEdit,
        onBulkEditFieldChange: onBulkEditFieldChange,
        onCompanyFSPFieldChange: onCompanyFSPFieldChange,
    }
}